import useCreateAccountWebhook from '@/api/mutations/account/useCreateAccountWebhook';
import useUpdateAccountWebhook from '@/api/mutations/account/useUpdateAccountWebhook';
import useGetAvailableWebhookEvents from '@/api/queries/useGetAvailableWebhookEvents';
import useGetPublishedWebhookApiVersions from '@/api/queries/useGetPublishedWebhookApiVersions';
import SubscribeToEventsSidebar from '@/ui/components/companySettings/components/SubscribeToEventsSidebar';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  AnchorButton,
  Button,
  Card,
  Conditional,
  EmptyState,
  Field,
  Fieldset,
  Form,
  Modal,
  RawInput,
  Select,
  Sidebar,
  SidebarProps as TSidebarProps,
  Text,
  useToastContext,
} from '@payaca/ambrosia';
import CopyTextButton from '@payaca/components/copyTextIconButton/CopyTextButton';

import AppearTransition from '@payaca/components/transitions/AppearTransition';
import { useFirstMountState } from '@payaca/hooks';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

type WebhookFormData = {
  name: string;
  url: string;
  events: string[];
  apiVersion?: string;
};

type CreateWebhookFormData = WebhookFormData & { secret: string };
export type EditWebhookFormData = WebhookFormData & { webhookId: string };

type CreateEditWebhookFormData =
  | ({ mode: 'create' } & CreateWebhookFormData)
  | ({ mode: 'edit' } & EditWebhookFormData);

const WebhookSchema = z.object({
  name: z.string().min(1),
  url: z.string().url().min(1),
  events: z.array(z.string()).min(1),
  apiVersion: z.string().optional(),
});

const WEBHOOK_VERSIONS_DOCS_URL =
  'https://docs.api.payaca.com/#description/webhook-versions';
const CHANGELOG_URL = 'https://docs.api.payaca.com/#description/changelog';

const CreateEditWebhookSchema = z.discriminatedUnion('mode', [
  WebhookSchema.extend({
    mode: z.literal('create'),
    secret: z.string().min(1),
  }),
  WebhookSchema.extend({
    mode: z.literal('edit'),
    webhookId: z.string().min(1),
  }),
]);

const SubscribedEventCard: FC<{
  appear: boolean;
  eventName: string;
  onRemove: () => void;
}> = (props) => {
  const { appear, eventName, onRemove } = props;

  /**
   * queries
   */
  const { data: availableWebhookEvents } = useGetAvailableWebhookEvents();

  /**
   * state
   */
  const [removing, setRemoving] = useState(false);

  const event = availableWebhookEvents?.availableWebhookEvents?.find(
    (event) => event.name === eventName
  );

  if (!event) return null;

  return (
    <AppearTransition
      appear={appear}
      key={event.name}
      show={!removing}
      afterLeave={onRemove}
    >
      <Card sizeVariant="sm">
        <Card.Body className="flex items-center justify-between gap-2.5">
          <div>
            <h4>{event.name}</h4>

            <p>{event.description}</p>
          </div>

          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setRemoving(true);
            }}
          >
            <p className="sr-only">Remove {event.name}</p>

            <UntitledIcon className="h-2.5 w-2.5" name="x-close" />
          </Button>
        </Card.Body>
      </Card>
    </AppearTransition>
  );
};

const ApiVersionField: FC<{
  publishedVersions: string[];
  savedVersion?: string;
}> = (props) => {
  const { publishedVersions, savedVersion } = props;

  const newestVersion = publishedVersions.at(-1);
  const versions =
    savedVersion && !publishedVersions.includes(savedVersion)
      ? [...publishedVersions, savedVersion].toSorted()
      : publishedVersions;
  const options = versions.toReversed().map((version) => ({
    label: version === newestVersion ? `${version} (newest)` : version,
    value: version,
  }));

  return (
    <Controller<CreateEditWebhookFormData, 'apiVersion'>
      name="apiVersion"
      render={({ field: { name, value, onChange } }) => {
        const selectedVersion = value ?? newestVersion;

        return (
          <Field name={name}>
            <Field.Label>API version</Field.Label>
            <Field.HelperText>
              Payloads sent to this webhook are built at this version. See{' '}
              <AnchorButton
                size="sm"
                openInNewTab
                variant="link"
                href={WEBHOOK_VERSIONS_DOCS_URL}
              >
                webhook versions
              </AnchorButton>
              .
            </Field.HelperText>

            <Select
              aria-label="API version"
              multiple={false}
              options={options}
              value={selectedVersion}
              onChange={onChange}
            />

            <Conditional
              condition={!!savedVersion && selectedVersion !== savedVersion}
            >
              <Alert colour="yellow" className="mt-2">
                <Text>
                  Changing the version changes the shape of the payloads this
                  webhook receives. Make sure your integration handles the new
                  shape before you save. See the{' '}
                  <AnchorButton
                    openInNewTab
                    variant="link"
                    href={CHANGELOG_URL}
                  >
                    changelog
                  </AnchorButton>{' '}
                  to understand what's changed.
                </Text>
              </Alert>
            </Conditional>
          </Field>
        );
      }}
    />
  );
};

export type TProps = {
  defaultValues?: EditWebhookFormData;
} & Omit<TSidebarProps, 'title' | 'behind'>;

const CreateEditWebhookSidebar: FC<TProps> = (props) => {
  const { defaultValues, onClose, ...rest } = props;

  /**
   * state
   */
  const { pushError } = useToastContext();
  const [showCompletedCreatedState, setShowCompletedCreatedState] =
    useState(false);
  const [isAddEventSidebarOpen, setIsAddEventSidebarOpen] = useState(false);
  const isFirstMount = useFirstMountState();

  /**
   * queries
   */
  const { data: publishedVersions } = useGetPublishedWebhookApiVersions();
  const newestVersion = publishedVersions?.at(-1);

  /**
   * mutations
   */
  const { mutateAsync: createAccountWebhook, isPending: isCreatingWebhook } =
    useCreateAccountWebhook();
  const { mutateAsync: updateAccountWebhook, isPending: isUpdatingWebhook } =
    useUpdateAccountWebhook();
  const isMutatingWebhook = isCreatingWebhook || isUpdatingWebhook;

  /**
   * form methods
   */
  const formMethods = useForm<CreateEditWebhookFormData>({
    defaultValues: defaultValues
      ? { mode: 'edit', ...defaultValues }
      : { events: [], mode: 'create' },
    resolver: zodResolver(CreateEditWebhookSchema),
  });
  const {
    register,
    watch,
    formState: { isDirty },
  } = formMethods;
  const isCreateMode = watch('mode') === 'create';
  const secretValue = watch('secret');

  /**
   * callbacks
   */
  const onSubmit = async (data: CreateEditWebhookFormData) => {
    try {
      if (data.mode === 'create') {
        await createAccountWebhook({
          name: data.name,
          url: data.url,
          events: data.events,
          secret: data.secret,
          apiVersion: data.apiVersion ?? newestVersion,
        });

        setShowCompletedCreatedState(true);
      } else {
        await updateAccountWebhook({
          webhookId: data.webhookId,
          name: data.name,
          url: data.url,
          events: data.events,
          apiVersion:
            data.apiVersion === defaultValues?.apiVersion
              ? undefined
              : data.apiVersion,
        });

        onClose?.();
      }
    } catch (e) {
      console.error(
        `Failed to ${data.mode === 'create' ? 'create' : 'update'} webhook:`,
        e
      );

      pushError(
        `Failed to ${
          data.mode === 'create' ? 'create' : 'update'
        } webhook. Please try again.`
      );
    }
  };

  const modalProps = {
    title: isCreateMode ? 'Create webhook' : 'Edit webhook',
    onClose,
    ...rest,
  };

  if (showCompletedCreatedState) {
    return (
      <Sidebar {...modalProps}>
        <Sidebar.Body className="p-4">
          <p className="mb-4">Webhook created successfully!</p>

          <p className="mb-2">
            We encourage you to use the secret you set to verify the requests
            are coming from us, see{' '}
            <AnchorButton
              openInNewTab
              variant="link"
              href="https://docs.api.payaca.com/#description/webhooks"
            >
              our webhook API docs
            </AnchorButton>{' '}
            for how to do so.
          </p>

          <h4 className="mb-2">Secret</h4>

          <CopyTextButton className="mb-4" textToCopy={secretValue} />

          <Alert colour="yellow">
            Make sure you make a copy of the secret. You will not be able to see
            it again.
          </Alert>
        </Sidebar.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button className="w-full" onClick={onClose}>
              Close
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Sidebar>
    );
  }

  return (
    <>
      <Sidebar
        behind={isAddEventSidebarOpen}
        warnUserOnClose={isDirty}
        {...modalProps}
      >
        <Form
          className="flex flex-col h-full"
          methods={formMethods}
          onSubmit={onSubmit}
        >
          <Sidebar.Body className="p-4">
            <Fieldset>
              <Field name="name">
                <Field.Label>Name</Field.Label>
                <Field.HelperText>
                  Your internal name for the webhook.
                </Field.HelperText>

                <RawInput {...register('name')} />
              </Field>

              <Field name="url">
                <Field.Label>URL</Field.Label>
                <Field.HelperText>
                  Your endpoint you wish us to hit when an event occurs.
                </Field.HelperText>

                <RawInput
                  {...register('url')}
                  placeholder="https://example.com/webhook"
                />
              </Field>

              <Controller<CreateEditWebhookFormData, 'events'>
                render={({ field: { name, value: events, onChange } }) => {
                  return (
                    <>
                      <Field name={name}>
                        <Field.Label>Subscribed events</Field.Label>

                        <Card>
                          <Card.Body className="flex flex-col gap-2.5 overflow-hidden">
                            {events.toSorted().map((eventName) => (
                              <SubscribedEventCard
                                appear={!isFirstMount}
                                key={eventName}
                                eventName={eventName}
                                onRemove={() => {
                                  onChange(
                                    events.filter((e) => e !== eventName)
                                  );
                                }}
                              />
                            ))}

                            <Conditional condition={events.length === 0}>
                              <EmptyState
                                iconName="announcement-03.3"
                                text="No subscribed events"
                              />
                            </Conditional>

                            <Button
                              className={clstx(events.length > 0 && 'mt-4')}
                              onClick={() => setIsAddEventSidebarOpen(true)}
                              variant="tertiary"
                              size="sm"
                            >
                              <UntitledIcon name="plus.3" className="w-4 h-4" />
                              Subscribe to event
                            </Button>
                          </Card.Body>
                        </Card>
                      </Field>
                      <SubscribeToEventsSidebar
                        zIndexLevel={2}
                        isOpen={isAddEventSidebarOpen}
                        onClose={() => setIsAddEventSidebarOpen(false)}
                        subscribedEvents={events}
                        onChange={onChange}
                      />
                    </>
                  );
                }}
                name="events"
              />

              <ApiVersionField
                publishedVersions={publishedVersions ?? []}
                savedVersion={defaultValues?.apiVersion}
              />

              {isCreateMode && (
                <Field name="secret">
                  <Field.Label>Secret</Field.Label>
                  <Field.HelperText>
                    We encourage you to use this to verify the requests are
                    coming from us, see{' '}
                    <AnchorButton
                      size="sm"
                      openInNewTab
                      variant="link"
                      href="https://docs.api.payaca.com/#description/webhooks"
                    >
                      our webhook API docs
                    </AnchorButton>{' '}
                    for how to do so.
                  </Field.HelperText>

                  <RawInput {...register('secret')} />
                </Field>
              )}
            </Fieldset>
          </Sidebar.Body>

          <Sidebar.Footer>
            <Button
              className="w-full"
              type="submit"
              isProcessing={isMutatingWebhook}
              disabled={isMutatingWebhook || !isDirty}
            >
              {isCreateMode ? 'Create' : 'Edit'} webhook
            </Button>
          </Sidebar.Footer>
        </Form>
      </Sidebar>
    </>
  );
};

export default CreateEditWebhookSidebar;
